import { useAuth } from '../auth/useAuth.js';
import { PET_ENABLED } from '../teachersPet/config.js';
// src/components/Navbar.jsx
// Router-aware navigation that collapses when its actual contents no longer fit.

import React, { useState, useRef, useLayoutEffect, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import LearningNotifications from './LearningNotifications.jsx';
import Logo from './Logo.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import { HelpFeedbackTrigger } from './HelpFeedback.jsx';
import {
    IconHome, IconLayers, IconBookOpen, IconCalendar,
    IconUser, IconCart, IconX, IconMenu, IconLogOut, IconFeather, IconEdit
} from './icons.jsx';

export const Navbar = ({ state, theme = 'gold', documentNavigation = false }) => {
    const { signOut } = useAuth();
    const [signOutError, setSignOutError] = useState('');
    const [signingOut, setSigningOut] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [compact, setCompact] = useState(true);
    const barRef = useRef(null);
    const brandRef = useRef(null);
    const linksRef = useRef(null);
    const location = useLocation();
    const routerNavigate = useNavigate();
    // Server-rendered pages share this bar, but their destinations need a full page load.
    const navigate = documentNavigation ? path => window.location.assign(path) : routerNavigate;
    const cartCount = state.cart.length;

    // Measure the real content (including fonts and account name), not a device label.
    // Keep the compact desktop row measurable, but hidden and out of the tab order.
    useLayoutEffect(() => {
        const measure = () => {
            const bar = barRef.current;
            const style = getComputedStyle(bar);
            const available = bar.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
            setCompact(window.innerWidth < 768 || brandRef.current.offsetWidth + linksRef.current.scrollWidth + 16 > available);
        };
        const observer = new ResizeObserver(measure);
        [barRef.current, brandRef.current, linksRef.current].forEach(el => observer.observe(el));
        measure();
        return () => observer.disconnect();
    }, []);
    useEffect(() => { setMenuOpen(false); }, [location.pathname, compact]);
    useEffect(() => {
        if (!menuOpen) return;
        const close = e => { if (e.key === 'Escape') setMenuOpen(false); };
        window.addEventListener('keydown', close);
        return () => window.removeEventListener('keydown', close);
    }, [menuOpen]);

    // Derive active nav-item id from current path (strip leading slash; '' → 'home').
    const currentPage = location.pathname.replace(/^\//, '') || 'home';

    // Theme color mapping
    const themes = {
        gold: { bg: 'bg-book-spine/95', border: 'border-book-accent/20', text: 'text-book-accent', muted: 'text-book-muted', accent: 'text-book-accent', button: 'bg-book-accent text-black hover:bg-book-accentLight' },
        burgundy: { bg: 'bg-burgundy-pageDark/95', border: 'border-burgundy-accent/25', text: 'text-burgundy-accent', muted: 'text-burgundy-muted', accent: 'text-burgundy-accent', button: 'bg-burgundy-accent text-burgundy-pageDark hover:bg-burgundy-accentLight' },
        midnight: { bg: 'bg-midnight-pageDark/95', border: 'border-midnight-accent/25', text: 'text-midnight-accent', muted: 'text-midnight-muted', accent: 'text-midnight-accent', button: 'bg-midnight-accent text-midnight-pageDark hover:bg-midnight-accentLight' },
        leather: { bg: 'bg-leather-pageDark/95', border: 'border-leather-accent/25', text: 'text-leather-accent', muted: 'text-leather-muted', accent: 'text-leather-accent', button: 'bg-leather-accent text-leather-pageDark hover:bg-leather-accentLight' },
        charcoal: { bg: 'bg-charcoal-pageDark/95', border: 'border-charcoal-accent/25', text: 'text-charcoal-accent', muted: 'text-charcoal-muted', accent: 'text-charcoal-accent', button: 'bg-charcoal-accent text-charcoal-pageDark hover:bg-charcoal-accentLight' }
    };
    const t = themes[theme] || themes.gold;

    const navItems = state.user ? [
        { id: 'home', label: 'Home', icon: IconHome, path: '/home' },
        { id: 'dashboard', label: 'Library', icon: IconLayers, path: '/dashboard' },
        { id: 'terminology', label: 'Terminology', icon: IconBookOpen, path: '/terminology' },
        { id: 'booking', label: 'Book a Session', icon: IconCalendar, path: '/booking' },
        { id: 'account', label: 'Account', icon: IconUser, path: '/account' }
    ] : [];

    return (
        <>
            <nav className={`btl-navbar fixed top-0 left-0 right-0 z-50 ${t.bg} backdrop-blur-md border-b ${t.border}`}>
                <div ref={barRef} className="max-w-screen-2xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between gap-4">
                    <div ref={brandRef} className="flex items-center gap-2 shrink-0">
                    <button onClick={() => navigate('/')} className="flex-shrink-0">
                        <Logo theme={theme} size="md" />
                    </button>

                    <LearningNotifications onNavigate={documentNavigation ? navigate : undefined} />
                    </div>
                    <div ref={linksRef} aria-hidden={compact || undefined} inert={compact ? '' : undefined}
                        className={`flex w-max shrink-0 items-center gap-1 ${compact ? 'absolute invisible pointer-events-none right-0 top-0' : ''}`}>
                        {navItems.map(item => (
                            <button key={item.id} onClick={() => navigate(item.path)}
                                className={`px-3 py-2 rounded-sm font-display text-xs transition-colors flex items-center gap-2 font-semibold ${
                                    currentPage === item.id ? `${t.accent}` : `${t.muted} hover:${t.accent.replace('text-', 'text-')}`
                                }`}>
                                <item.icon className="w-4 h-4" />
                                {item.label}
                            </button>
                        ))}

                        {/* Public server-rendered Blog: use a real link, not SPA navigation. */}
                        <a href="/blog"
                            className={`px-3 py-2 rounded-sm font-display text-xs transition-colors flex items-center gap-2 font-semibold ${t.muted} hover:${t.accent}`}>
                            <IconFeather className="w-4 h-4 shrink-0" />Teaching Notes
                        </a>
                        {/* Personal Statements -- a PUBLIC funnel link shown to
                            EVERYONE (navItems is signed-in-only, so this sits
                            outside it). Same typography + gold-hover as the items. */}
                        <button onClick={() => navigate('/personal-statement')}
                            className={`px-3 py-2 rounded-sm font-display text-xs transition-colors flex items-center gap-2 font-semibold ${
                                currentPage === 'personal-statement' ? `${t.accent}` : `${t.muted} hover:${t.accent.replace('text-', 'text-')}`
                            }`}>
                            <IconEdit className="w-4 h-4 shrink-0" />Personal Statements
                        </button>
                        {state.user && PET_ENABLED && <button onClick={() => navigate('/teachers-pet')}
                            className={`px-3 py-2 rounded-sm font-display text-xs transition-colors flex items-center gap-2 font-semibold ${currentPage === 'teachers-pet' ? t.accent : `${t.muted} hover:${t.accent}`}`}>
                            <IconHome className="w-4 h-4 shrink-0" />Teacher’s Pet
                        </button>}

                        <HelpFeedbackTrigger variant="nav" className={t.muted} />

                        {/* Theme switch -- available to everyone, incl. signed-out visitors.
                            compact = tighter so "Minimalist" never clips beside the cart. */}
                        <ThemeToggle className="ml-2" compact />

                        {state.user ? (
                            <>
                                <button onClick={() => navigate('/cart')} className={`relative ml-2 p-2 ${t.muted} hover:${t.accent} transition-colors`}>
                                    <IconCart className="w-5 h-5" />
                                    {cartCount > 0 && <span className={`absolute -top-1 -right-1 ${t.button.split(' ')[0]} ${t.button.split(' ')[1]} text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center`}>{cartCount}</span>}
                                </button>
                                <div className={`btl-nav-account ml-2 px-4 py-2 flex items-center gap-2 border ${t.border} rounded-sm`}>
                                    <div className={`w-6 h-6 flex-shrink-0 rounded-full ${t.button.split(' ')[0]} flex items-center justify-center text-xs font-bold ${t.button.split(' ')[1]}`}>
                                        {state.user.name?.[0]?.toUpperCase() || 'U'}
                                    </div>
                                    <span title={state.user.name} className={`max-w-32 truncate font-serif italic text-sm ${t.text}`}>{state.user.name}</span>
                                </div>
                            </>
                        ) : (
                            <button onClick={() => navigate('/login')} className={`ml-2 px-5 py-2 ${t.button} font-display text-xs transition-colors btn-sweep font-semibold`}>
                                Sign In
                            </button>
                        )}
                    </div>

                    {/* Mobile */}
                    <div className={`${compact ? 'flex' : 'hidden'} shrink-0 items-center gap-2`}>
                        {state.user && (
                            <button aria-label="Shopping cart" onClick={() => navigate('/cart')} className={`relative p-2 ${t.muted}`}>
                                <IconCart className="w-5 h-5" />
                                {cartCount > 0 && <span className={`absolute -top-1 -right-1 ${t.button.split(' ')[0]} ${t.button.split(' ')[1]} text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center`}>{cartCount}</span>}
                            </button>
                        )}
                        <button aria-label={menuOpen ? 'Close menu' : 'Open menu'} aria-expanded={menuOpen} aria-controls="site-navigation-menu" onClick={() => setMenuOpen(!menuOpen)} className={`${t.accent} p-2`}>
                            {menuOpen ? <IconX className="w-6 h-6" /> : <IconMenu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {compact && menuOpen && (
                    <div id="site-navigation-menu" className={`btl-navbar-panel max-h-[calc(100dvh-4rem)] overflow-y-auto ${t.bg} border-t ${t.border}`}>
                        <div className="px-4 py-4 flex flex-col gap-1">
                            {signOutError && <p role="alert" className="px-4 py-2 text-sm">{signOutError}</p>}
                            {/* Theme switch (mobile) -- available to everyone. */}
                            <div className="pb-2"><ThemeToggle compact /></div>
                            {state.user && navItems.map(item => (
                                <button key={item.id} onClick={() => { navigate(item.path); setMenuOpen(false); }}
                                    className={`text-left px-4 py-3 font-display text-xs flex items-center gap-3 font-semibold ${currentPage === item.id ? t.accent : t.muted}`}>
                                    <item.icon className="w-4 h-4" />{item.label}
                                </button>
                            ))}
                            <a href="/blog" onClick={() => setMenuOpen(false)}
                                className={`text-left px-4 py-3 font-display text-xs flex items-center gap-3 font-semibold ${t.muted} hover:${t.accent}`}>
                                <IconFeather className="w-4 h-4 shrink-0" />Teaching Notes
                            </a>
                            {/* Personal Statements -- PUBLIC, shown to everyone (outside
                                the signed-in navItems guard). */}
                            <button onClick={() => { navigate('/personal-statement'); setMenuOpen(false); }}
                                className={`text-left px-4 py-3 font-display text-xs flex items-center gap-3 font-semibold ${currentPage === 'personal-statement' ? t.accent : t.muted}`}>
                                <IconEdit className="w-4 h-4 shrink-0" />Personal Statements
                            </button>
                            {state.user && PET_ENABLED && <button onClick={() => { navigate('/teachers-pet'); setMenuOpen(false); }}
                                className={`text-left px-4 py-3 font-display text-xs flex items-center gap-3 font-semibold ${currentPage === 'teachers-pet' ? t.accent : t.muted}`}>
                                <IconHome className="w-4 h-4 shrink-0" />Teacher’s Pet
                            </button>}
                            <HelpFeedbackTrigger variant="nav" className={t.muted} />
                            {!state.user ? (
                                <button onClick={() => { navigate('/login'); setMenuOpen(false); }} className={`mt-2 px-4 py-3 ${t.button} font-display text-xs font-semibold`}>Sign In</button>
                            ) : (
                                <button disabled={signingOut} onClick={async () => { setSigningOut(true); setSignOutError(''); try { await signOut(); navigate('/home'); setMenuOpen(false); } catch { setSignOutError('Could not sign out. Please try again.'); } finally { setSigningOut(false); } }} className={`text-left px-4 py-3 ${t.muted} font-display text-xs flex items-center gap-3 font-semibold`}>
                                    <IconLogOut className="w-4 h-4" />{signingOut ? 'Signing out…' : 'Sign Out'}
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </nav>
            <div className="h-16"></div>
        </>
    );
};

export default Navbar;
